<template>
  <!-- Public "Поделиться" link from an active route registration (see
       ProfileView.vue's shareActiveRegistration) - standalone page, no
       account, no tab shell. (Публичный профиль /user/<имя> - не отдельная
       страница, а панель поверх карты: UserProfilePanel / MobileProfileSheet ниже.) -->
  <HikeCardView v-if="isHikeCardRoute" :token="route.params.token as string" />

  <!-- Архив обновлений и страница документа - тот же standalone-приём. -->
  <NewsView v-else-if="isNewsRoute" />
  <NewsDocumentView v-else-if="isNewsDocumentRoute" :slug="route.params.slug as string" />

  <!-- Standalone POC tool (see router/index.ts) - own minimal layout, no
       tab shell/nav/login overlays, same treatment as the hike card
       page above. Lazy-loaded (exifr) so its bundle never downloads for
       regular visitors. -->
  <ImportPointsView v-else-if="isImportPointsRoute" />

  <!-- MikroTik Hotspot captive-portal landing page (Мынжылкы) - same
       standalone treatment as the routes above. -->
  <CaptiveView v-else-if="isCaptiveRoute" />

  <!-- Telegram Mini App (/tg/register) - same standalone treatment, own
       in-memory token client (tgApi.ts), no site session/overlays. -->
  <TgRegisterView v-else-if="isTgRegisterRoute" />

  <!-- Email-регистрация/сброс пароля - ссылка из письма, тот же
       standalone-паттерн, что и остальные страницы выше. -->
  <EmailVerifyView v-else-if="isVerifyEmailRoute" />
  <ResetPasswordView v-else-if="isResetPasswordRoute" @show-login="onResetPasswordShowLogin" />

  <!-- OAuth-редиректы (Google/Telegram OIDC) - см. router/index.ts и
       composables/useOAuthLogin.ts. -->
  <OAuthCallbackView v-else-if="isGoogleCallbackRoute" provider="google" />
  <OAuthCallbackView v-else-if="isTelegramOidcCallbackRoute" provider="telegram" />

  <!-- Единая форма входа (/login?return=<url>) - см. LoginPageView.vue. -->
  <LoginPageView v-else-if="isLoginRoute" />

  <div v-else class="h-[100dvh] w-full flex flex-col bg-[hsl(var(--background))] text-[hsl(var(--foreground))] overflow-hidden">

    <!-- Красный баннер "регистрация скоро истекает / срок прошёл" - обычный
         элемент потока над оболочкой (не fixed): остальной layout сжимается
         под ним, ничего не перекрывается. Только в оболочке с вкладками и
         только для вошедших (стор опрашивается ниже). -->
    <ActiveRegistrationBanner v-if="auth.isAuthenticated" class="shrink-0" />

    <div class="flex-1 min-h-0 flex">
    <SkipLink />

    <!-- Desktop left nav rail (md+). В режиме редактора маршрутов вкладки
         заменяются инструментами рисования, логотип закрывает редактор. -->
    <AppRail
      :active="activeTab"
      :tabs="tabs"
      :badges="tabBadges"
      :logo="logoMark"
      :logo-label="routeEditorActive ? $t('map.routeEditor.close') : $t('app.tabs.map')"
      @select="selectTab"
      @feedback="feedback.open('main-nav')"
      @logo="routeEditorActive ? closeRouteEditor() : selectTab('map')"
    >
      <!-- Пока открыт редактор, вкладки скрыты: все его кнопки - в нижней
           панели и правой панели инструментов, рейке остаётся только логотип. -->
      <template v-if="routeEditorActive" #override>
        <span aria-hidden="true" />
      </template>
      <template #bottom>
        <ThemeToggle compact />
        <LanguageSwitcher compact />
      </template>
    </AppRail>

    <!-- Main content -->
    <div class="flex-1 min-w-0 flex flex-col h-full overflow-hidden">

      <!-- Page content -->
      <main id="main" tabindex="-1" class="flex-1 relative overflow-hidden outline-none">
        <!-- Map tab: browses the live map canvas underneath, with an
             overlaid routes panel. Live moved to its own standalone page
             below (no longer sharing this shell) once nodes/weather/cameras
             + the new timelapse/hello feeds needed a real scrollable page
             instead of a fixed-width overlay panel. -->
        <div v-show="activeTab === 'map'" class="h-full w-full flex">
          <!-- Sidebar: routes panel, desktop-only (narrow, fixed width) -
               mobile uses MobileMapOverlay below instead. Полностью скрыта в
               режиме редактора маршрутов - поиск/список маршрутов там не
               нужен (см. RouteEditorBottomPanel вместо неё). -->
          <aside
            v-if="ready && !routeEditorActive"
            v-show="!listMode"
            class="absolute top-0 left-0 bottom-0 z-40 flex-col bg-[hsl(var(--card))] border-r border-[hsl(var(--border))] shadow-lg transition-transform duration-[450ms] ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform"
            :class="[
              'hidden md:flex w-[380px] xl:w-[420px]',
              sidebarCollapsed ? '-translate-x-full' : 'translate-x-0',
            ]"
          >
            <!-- Карточки - стопкой: точка поверх маршрута (маршрут под ней
                 остаётся смонтированным, «Назад» точки снимает только её).
                 «Назад»/крестик - useSearchNavigation (back/close). -->
            <PointCard
              v-if="selectedPointId && !isPhone"
              :id="selectedPointId"
              @back="searchNav.back"
              @close="searchNav.close"
              @open-route="(id) => searchNav.openFromCard({ route: id })"
              @open-point="(id) => searchNav.openFromCard({ point: id })"
            />
            <RouteDetailsPanel
              v-if="hasRouteSelection"
              v-show="!selectedPointId"
              :selectedRouteId="selectedRouteId"
              :cursorDistanceKm="routeCursorKm"
              @back="searchNav.back"
              @close="searchNav.close"
              @show-login="loginGate.open()"
              @update:cursorDistanceKm="routeCursorKm = $event"
              @open-profile="searchNav.openProfile"
            />
            <!-- Профиль автора (/user/<имя>) - в том же слоте, что и карточка
                 маршрута; точка поверх него - стопкой, как над маршрутом. -->
            <UserProfilePanel
              v-if="profileUsername && !hasRouteSelection && !isPhone"
              v-show="!selectedPointId"
              :username="profileUsername"
              @back="searchNav.backFromProfile"
              @close="searchNav.close"
              @open-route="searchNav.openRoute"
            />
            <!-- Не на телефоне: aside там лишь скрыт CSS, а вторая копия
                 SearchPanel дублировала бы каждый /search мобильного поиска. -->
            <LeftPanel
              v-if="!hasRouteSelection && !selectedPointId && !profileUsername && !isPhone"
              @open-route="searchNav.openRoute"
              @open-point="searchNav.openPoint"
            />
          </aside>

          <!-- Sidebar collapse handle -->
          <button
            v-if="ready && !routeEditorActive && !listMode"
            @click="sidebarCollapsed = !sidebarCollapsed"
            class="hidden md:flex absolute top-1/2 -translate-y-1/2 z-50 w-6 h-16 items-center justify-center bg-[hsl(var(--card))] border border-[hsl(var(--border))] border-l-0 rounded-r-md shadow-md hover:bg-[hsl(var(--muted))] transition-[left] duration-[450ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
            :class="sidebarCollapsed ? 'left-0' : 'left-[380px] xl:left-[420px]'"
            :aria-label="sidebarCollapsed ? $t('app.sidebar.show') : $t('app.sidebar.hide')"
          >
            <MorphIcon :icon="sidebarCollapsed ? MorphChevronRight : MorphChevronLeft" class="w-4 h-4" />
          </button>

          <!-- Map canvas - full bleed, shared between Map and Live tabs.
               В режиме «Список» карта под ним inert: вне порядка табуляции и
               дерева доступности. -->
          <div class="absolute inset-0 z-0" :inert="listMode || undefined" :aria-hidden="listMode || undefined">
            <MapView
              v-if="ready"
              ref="mapViewRef"
              :selectedRouteId="activeTab === 'map' ? selectedRouteId : null"
              :cursorDistanceKm="routeCursorKm"
              @route-select="handleRouteSelect"
              @open-camera="openCamera"
              @open-node="openNode"
              @update:cursorDistanceKm="routeCursorKm = $event"
              @show-login="loginGate.open()"
              @point-sheet-change="pointSheetOpen = $event"
              @point-tap="searchNav.openPointFromMap"
              @map-interact="collapseMobileCard"
            />
            <div v-else class="flex items-center justify-center h-full text-[hsl(var(--muted-foreground))] text-sm">
              {{ $t('app.mapLoading') }}
            </div>
          </div>

          <!-- Режим «Список» (/map?view=list): каталог поверх карты, карта под
               ним остаётся смонтированной. -->
          <div v-show="listMode" class="absolute inset-0 z-[60] overflow-y-auto bg-[hsl(var(--background))]">
            <RoutesView :active="listMode" @open-route="openRouteOnMap" @show-login="loginGate.open()" />
          </div>

          <!-- Mobile: панель поиска над таб-баром (одна поверхность с ним) и
               развёрнутый поиск на весь экран - только вкладка «Маршруты».
               Скрыта, пока открыта карточка объекта (?route= / ?point=) -
               вместо неё MobileRouteDetailSheet ниже (как на десктопе
               RouteDetailsPanel вместо LeftPanel). Выбор в поиске - шаг в
               истории (useSearchNavigation), «назад» возвращает в поиск. -->
          <MobileMapOverlay
            v-if="mobileOverlayMounted"
            v-show="mobileOverlayShown"
            @open-route="searchNav.openRoute"
            @open-point="searchNav.openPoint"
          />

          <!-- Mobile: route detail bottom sheet, shown in place of the
               overlay above once a route is selected (map pin or list tap). -->
          <MobileRouteDetailSheet
            v-if="ready && activeTab === 'map' && selectedRouteId && !listMode"
            v-show="!pointSheetOpen && !selectedPointId"
            ref="mobileDetailRef"
            :routeId="selectedRouteId"
            :covered="!!selectedPointId"
            :cursorDistanceKm="routeCursorKm"
            @back="searchNav.back"
            @close="searchNav.close"
            @show-login="loginGate.open()"
            @update:cursorDistanceKm="routeCursorKm = $event"
            @open-profile="searchNav.openProfile"
          />

          <!-- Mobile: шторка профиля автора (/user/<имя>) вместо панели поиска. -->
          <MobileProfileSheet
            v-if="ready && activeTab === 'map' && profileUsername && !hasRouteSelection && !listMode && isPhone"
            v-show="!pointSheetOpen && !selectedPointId"
            ref="mobileProfileRef"
            :username="profileUsername"
            :covered="pointSheetOpen || !!selectedPointId"
            @back="searchNav.backFromProfile"
            @close="searchNav.close"
            @open-route="searchNav.openRoute"
          />

          <!-- Mobile: шторка карточки точки (?point=), поверх шторки маршрута. -->
          <MobilePointSheet
            v-if="ready && activeTab === 'map' && selectedPointId && !listMode && isPhone"
            v-show="!pointSheetOpen"
            ref="mobilePointRef"
            :pointId="selectedPointId"
            @back="searchNav.back"
            @close="searchNav.close"
            @open-route="(id) => searchNav.openFromCard({ route: id })"
            @open-point="(id) => searchNav.openFromCard({ point: id })"
          />
        </div>

        <!-- Live tab: standalone page (nodes/weather/cameras + per-node
             timelapse feed + #ПриветEcoway), no longer an overlay panel on
             the Map shell above. -->
        <div v-show="activeTab === 'live'" class="h-full w-full overflow-y-auto">
          <LiveHomeView :active="activeTab === 'live'" @show-login="loginGate.open()" @show-about="showAbout = true" />
        </div>

        <!-- Безопасность: регистрация выходов, экстренный контакт, памятка. -->
        <div v-show="activeTab === 'safety'" class="h-full w-full overflow-y-auto">
          <SafetyView :active="activeTab === 'safety'" @show-login="loginGate.open()" />
        </div>

        <!-- Profile tab -->
        <div v-show="activeTab === 'profile'" class="h-full w-full overflow-y-auto">
          <ProfileView
            @show-login="loginGate.open()"
            @open-on-map="openRouteOnMap"
            @show-about="showAbout = true"
          />
        </div>

        <!-- Admin tab (role=admin only) - Element Plus, scoped behind an
             async import so it never loads for public visitors.
             [contain:layout] makes this outer div a containing block for
             the admin shell's fixed-position overlays (mobile nav drawer,
             every el-dialog) - without it they'd position against the
             browser viewport and escape over this app's own left nav rail.
             It's deliberately on its own non-scrolling wrapper, separate
             from the inner overflow-y-auto div: contain:layout's
             containing block tracks the scrollable content of whichever
             element it's on, not that element's stable viewport, so a
             dialog opened while scrolled partway down a long admin list
             would render off-screen (verified: the overlay's rect shifted
             by exactly -scrollTop) if both were the same element. -->
        <div v-show="activeTab === 'admin'" class="h-full w-full [contain:layout]">
          <div class="h-full w-full overflow-y-auto">
            <AdminApp v-if="activeTab === 'admin'" />
          </div>
        </div>
      </main>

      <!-- Global copyright footer - shrinks <main>'s flex-1 space by a few
           px on every tab (incl. the full-bleed map) rather than being
           scoped to one page, per explicit request that it live at the
           bottom of the whole app, not just one view. Desktop-only: on
           mobile it competed with the bottom tab bar for the same cramped
           strip of screen. -->
      <footer class="hidden md:block shrink-0 py-1 text-center text-[10px] text-[hsl(var(--muted-foreground))]">
        {{ $t('app.footer.copyright', { year: currentYear }) }}
      </footer>

      <!-- Mobile bottom tab bar (hidden md+) - swaps to the selected route's
           action buttons (save/share/register/report) while its detail
           sheet is open, mirroring the reference design's mobile bottom-bar
           override and freeing the sheet itself from needing an inline
           button row (see RouteDetailsPanel.vue's compact prop). -->
      <!-- Над вкладками на карте вплотную стоит панель поиска MobileMapOverlay:
           общий фон, верхняя граница - у неё (joined). Пока поиск развёрнут на
           весь экран, бар под его слоем и вне фокуса (inert). -->
      <nav
        class="md:hidden shrink-0 bg-card safe-bottom z-50"
        :class="mobileDockShown || routeBarShown ? '' : 'border-t border-[hsl(var(--border))]'"
        :inert="mobileSearchOpen || undefined"
      >
        <RouteEditorActionBar
          v-if="routeEditorActive"
          :editor="mapViewRef!.routeEditor"
          :is3D="mapViewRef!.is3D"
          @close="closeRouteEditor"
          @save="mapViewRef!.openSaveDialog(false)"
          @save-as="mapViewRef!.openSaveDialog(true)"
          @export="mapViewRef!.handleEditorExport()"
          @import="(tab) => mapViewRef!.handleEditorImportOpen(tab)"
          @toggle-3d="mapViewRef!.toggle3D()"
          @new="mapViewRef!.handleEditorNew()"
        />
        <!-- Открыта шторка маршрута - вместо вкладок его действия
             (store/routeActions.ts публикует RouteDetailsPanel); вернуться к
             вкладкам - «Назад» в шторке. -->
        <RouteActionRow
          v-else-if="routeActions.current && activeTab === 'map' && !listMode && !selectedPointId"
          :saved="routeActions.current.saved"
          :can-download="routeActions.current.canDownload"
          @save="routeActions.current.save()"
          :has-start="routeActions.current.hasStart"
          @share="routeActions.current.share()"
          @copy-start="routeActions.current.copyStart?.()"
          @open-2gis="routeActions.current.open2gis?.()"
          @open-google-maps="routeActions.current.openGoogleMaps?.()"
          @register="routeActions.current.register()"
          @download="routeActions.current.download()"
        />
        <AppTabBar v-else :active="activeTab" :tabs="tabs" :badges="tabBadges" :joined="mobileDockShown" @select="selectTab" @feedback="feedback.open('main-nav')" />
      </nav>
    </div>

    <!-- Cookie consent banner - shown until the visitor makes a choice, and
         reopenable from Profile > Settings (store.openSettings()). Rendered
         above InstallAppBanner (higher z-index) since it's the
         legally-required one of the two. -->
    <CookieConsentBanner />

    <!-- "Install app" banner - only ever renders when the browser actually
         fired beforeinstallprompt (Chromium/Android; no equivalent on iOS
         Safari/Firefox), see useInstallPrompt.ts. -->
    <InstallAppBanner />

    <!-- Login modal -->
    <LoginModal v-if="loginGate.isOpen" @close="loginGate.close()" @logged="onLogged" />

    <!-- About-the-project page - lifted here (rather than local to
         ProfileView) since LiveHomeView's credits banner opens it too. -->
    <AboutProjectView v-if="showAbout" @close="showAbout = false" />

    <!-- Profile-completion gate: shown for any logged-in user (new signup
         or an existing account, incl. the seeded admin) who hasn't finished
         their profile yet - reactive off auth.termsAcceptedAt, which
         EditProfileDialog's own submit sets via auth.setProfile(), so this
         closes itself with no extra "completed" event needed. -->
    <EditProfileDialog v-if="showOnboarding" mode="onboarding" />

    <!-- Feedback form - callable from anywhere via useFeedbackStore().open(source),
         mounted once here rather than locally in each caller. -->
    <FeedbackDialog v-if="feedback.isOpen" :source="feedback.source" @close="feedback.close()" />

    <!-- "Следующий твой" (#ПриветEcoway) - из шапки раздела Live и по прямой
         ссылке /hello/join (QR на локации, см. watch на route.name ниже). -->
    <HelloFeedWaveDialog v-if="helloFeedWave.isOpen" @close="helloFeedWave.close()" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import {
  Map as MapIcon, Radio, User, ShieldCheck,
} from "lucide-vue-next";
import { MorphIcon } from "morphicons/vue";
import { ChevronLeft as MorphChevronLeft, ChevronRight as MorphChevronRight } from "lucide";
import logoMark from "./assets/logo-mark.png";
import MapView from "./components/MapView.vue";
import LeftPanel from "./components/LeftPanel.vue";
import RouteDetailsPanel from "./components/RouteDetailsPanel.vue";
import LoginModal from "./components/LoginModal.vue";
import LoginPageView from "./components/views/LoginPageView.vue";
import { completePendingReturn } from "./composables/useLoginReturn";
import AboutProjectView from "./components/views/AboutProjectView.vue";
import InstallAppBanner from "./components/InstallAppBanner.vue";
import ActiveRegistrationBanner from "./components/ActiveRegistrationBanner.vue";
import CookieConsentBanner from "./components/CookieConsentBanner.vue";
import FeedbackDialog from "./components/FeedbackDialog.vue";
import HelloFeedWaveDialog from "./components/HelloFeedWaveDialog.vue";
import MobileMapOverlay from "./components/MobileMapOverlay.vue";
import MobileRouteDetailSheet from "./components/MobileRouteDetailSheet.vue";
import UserProfilePanel from "./components/UserProfilePanel.vue";
import MobileProfileSheet from "./components/MobileProfileSheet.vue";
import PointCard from "./components/point/PointCard.vue";
import MobilePointSheet from "./components/point/MobilePointSheet.vue";
import { useIsPhone } from "./composables/useIsPhone";
import EditProfileDialog from "./components/EditProfileDialog.vue";
import LanguageSwitcher from "./components/LanguageSwitcher.vue";
import ThemeToggle from "./components/ThemeToggle.vue";
import RouteEditorActionBar from "./components/RouteEditorActionBar.vue";
import AppRail from "./components/shell/AppRail.vue";
import AppTabBar from "./components/shell/AppTabBar.vue";
import SkipLink from "./components/shell/SkipLink.vue";
import { isListMode, tabFromPath, type TabKey } from "./utils/navigation";
import api from "./api";
import { isAuthRejection } from "./utils/authErrors";
import { useAuthStore } from "./store/auth";
import { setAdminTrackingExcluded, trackEvent } from "./analytics";
import { useSavedRoutesStore } from "./store/savedRoutes";
import { useCookieConsentStore } from "./store/cookieConsent";
import { useActiveRegistrationStore } from "./store/activeRegistration";
import { useFeedbackStore } from "./store/feedback";
import { useNotificationsStore } from "./store/notifications";
import { routePath, slugFromPath, useRouteLinksStore } from "./store/routeLinks";
import { useHelloFeedWaveStore } from "./store/helloFeedWave";
import { useLoginGateStore } from "./store/loginGate";
import { useRouteActionsStore } from "./store/routeActions";
import { useSearchSessionStore } from "./store/searchSession";
import { useSearchNavigation } from "./composables/useSearchNavigation";
import RouteActionRow from "./components/route/RouteActionRow.vue";


// Element Plus подключается только через эту асинхронную границу — его JS+CSS
// скачиваются лишь при открытии вкладки админки (см. CLAUDE.md "Admin panel").
const AdminApp = defineAsyncComponent(() => import("./admin/AdminApp.vue"));
const ImportPointsView = defineAsyncComponent(() => import("./components/views/ImportPointsView.vue"));
const CaptiveView = defineAsyncComponent(() => import("./components/views/CaptiveView.vue"));
const TgRegisterView = defineAsyncComponent(() => import("./components/views/TgRegisterView.vue"));
// Map — вкладка по умолчанию (см. activeTab ниже), поэтому только она
// остаётся в основном чанке. Live тянет за собой hls.js (~1.3 МБ decoded —
// самый тяжёлый модуль во всём бандле, см. NodesSection.vue → HlsPlayer.vue)
// и весь остальной контент ленты; Explore/Profile тоже не нужны для первой
// отрисовки Map. HikeCard/EmailVerify/ResetPassword/OAuthCallback —
// взаимоисключающие отдельные роуты (см. v-else-if цепочку в шаблоне), тот
// же паттерн, что уже был у ImportPointsView/AdminApp выше.
const LiveHomeView = defineAsyncComponent(() => import("./components/views/LiveHomeView.vue"));
const RoutesView = defineAsyncComponent(() => import("./components/views/RoutesView.vue"));
const SafetyView = defineAsyncComponent(() => import("./components/views/SafetyView.vue"));
const ProfileView = defineAsyncComponent(() => import("./components/views/ProfileView.vue"));
const HikeCardView = defineAsyncComponent(() => import("./components/views/HikeCardView.vue"));
const NewsView = defineAsyncComponent(() => import("./components/views/NewsView.vue"));
const NewsDocumentView = defineAsyncComponent(() => import("./components/views/NewsDocumentView.vue"));
const EmailVerifyView = defineAsyncComponent(() => import("./components/views/EmailVerifyView.vue"));
const ResetPasswordView = defineAsyncComponent(() => import("./components/views/ResetPasswordView.vue"));
const OAuthCallbackView = defineAsyncComponent(() => import("./components/views/OAuthCallbackView.vue"));

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const auth = useAuthStore();
const cookieConsent = useCookieConsentStore();
const feedback = useFeedbackStore();
const activeRegistration = useActiveRegistrationStore();

// Опрос активной регистрации (раз в минуту + при возврате на вкладку) идёт
// только пока пользователь вошёл; при выходе таймеры снимаются.
watch(
  () => auth.isAuthenticated,
  (loggedIn) => (loggedIn ? activeRegistration.start() : activeRegistration.stop()),
  { immediate: true },
);
onBeforeUnmount(() => activeRegistration.stop());
const helloFeedWave = useHelloFeedWaveStore();

// Красная точка на вкладке «Я»: счётчик непрочитанного ведётся, пока пользователь
// вошёл; обновляется по SSE-событию «notifications». При выходе - сброс.
const notifications = useNotificationsStore();
notifications.subscribe();
watch(
  () => auth.isAuthenticated,
  (loggedIn) => (loggedIn ? notifications.fetchCount() : notifications.reset()),
  { immediate: true },
);
const tabBadges = computed(() => ({ profile: notifications.unreadCount > 0 }));

// Аккаунт МЧС (роль "mes") без admin тоже получает вкладку админки — открывается
// тот же AdminShell.vue, который сам ограничивает навигацию до регистраций маршрутов.
const canOpenAdminPanel = computed(() => auth.isAdmin || auth.roles.includes("mes"));

// Визиты админов не должны попадать в статистику (GA/Plausible) - реактивно
// (а не один раз при монтировании), т.к. admin-статус текущего пользователя
// подтягивается асинхронно (GET /auth/me) и может стать известен уже после
// того, как согласие на cookie успело инициализировать оба скрипта.
watch(() => auth.isAdmin, setAdminTrackingExcluded, { immediate: true });

// Пять постоянных вкладок для всех - один массив питает и рейку, и нижний
// бар. Админка больше не вкладка: вход в неё - ссылка в «Я › Настройки».
const tabs = computed(() => [
  { key: "map" as TabKey, label: t("app.tabs.map"), icon: MapIcon },
  { key: "live" as TabKey, label: t("app.tabs.live"), icon: Radio },
  { key: "safety" as TabKey, label: t("app.tabs.safety"), icon: ShieldCheck },
  { key: "profile" as TabKey, label: t("app.tabs.me"), icon: User },
]);
const savedRoutes = useSavedRoutesStore();
const loginGate = useLoginGateStore();
const routeActions = useRouteActionsStore();
const showAbout = ref(false);
const ready = ref(false);
const sidebarCollapsed = ref(false);
// True после того как refreshMe() отработал — страж /admin/* ждёт этого, чтобы
// жёсткая перезагрузка не отбрасывала пользователя раньше, чем auth.roles
// (не персистится, в отличие от accessToken) успеет загрузиться.
const authChecked = ref(false);

// Мост к внутреннему состоянию редактора маршрутов внутри MapView.vue - App.vue
// подменяет свою навигационную панель (левая рейка на десктопе, нижний бар на
// мобильных) меню Файл/Вид, пока редактор активен, поэтому ему нужен доступ к
// состоянию редактора и его 3D-тумблеру через defineExpose в MapView.vue.
const mapViewRef = ref<InstanceType<typeof MapView> | null>(null);
const routeEditorActive = computed(() => Boolean(mapViewRef.value?.routeEditor.active.value));
// Подтверждение выхода при несохранённых изменениях - модал в MapView.vue
// (handleEditorClose сам спрашивает, если нужно).
const closeRouteEditor = () => {
  mapViewRef.value?.handleEditorClose();
};

// activeTab/selectedRouteId выводятся из роута, а не хранятся отдельными ref —
// переключение вкладки и выбор маршрута всегда настоящая навигация. App.vue
// вручную переключает v-show по вкладкам вместо <router-view> из-за стоимости
// пересоздания MapLibre/HLS.
const activeTab = computed<TabKey>(() => tabFromPath(route.path));
// Режим «Список» вкладки «Маршруты» (/map?view=list).
const listMode = computed(() => activeTab.value === "map" && isListMode(route.query));

// Публичный профиль автора (/user/<имя>) - панель поверх карты (вкладка «Маршруты»,
// tabFromPath не знает "user").
const profileUsername = computed<string | null>(() => {
  if (route.name !== "publicProfile") return null;
  const raw = route.params.username;
  const v = Array.isArray(raw) ? raw[0] : raw;
  return v ? String(v) : null;
});
const isHikeCardRoute = computed(() => route.name === "hikeCard");
const isNewsRoute = computed(() => route.name === "news");
const isNewsDocumentRoute = computed(() => route.name === "newsDocument");
const isImportPointsRoute = computed(() => route.name === "importPoints");
const isCaptiveRoute = computed(() => route.name === "captive");
const isTgRegisterRoute = computed(() => route.name === "tgRegister");
const isVerifyEmailRoute = computed(() => route.name === "verifyEmail");
const isResetPasswordRoute = computed(() => route.name === "resetPassword");
const isGoogleCallbackRoute = computed(() => route.name === "googleCallback");
const isTelegramOidcCallbackRoute = computed(() => route.name === "telegramOidcCallback");
const isLoginRoute = computed(() => route.name === "login");

const onResetPasswordShowLogin = () => {
  router.replace("/map");
  loginGate.open();
};

// Прямая ссылка /hello/join?node=<id> (QR на конкретной ноде) - нормализуем
// URL на /live (где живёт лента #ПриветEcoway), и только после этого
// открываем модалку, чтобы её запись в истории (useHistoryBackClose) не была
// затёрта replace'ом. node достаём из query ДО replace, иначе он вместе с
// остальным query потеряется при переходе на чистый /live.
watch(
  () => route.name,
  (name) => {
    if (name === "helloJoin") {
      const nodeParam = route.query.node;
      const nodeId = Number(Array.isArray(nodeParam) ? nodeParam[0] : nodeParam);
      router.replace("/live").then(() => helloFeedWave.open(Number.isFinite(nodeId) ? nodeId : null));
    }
  },
  { immediate: true }
);

const currentYear = new Date().getFullYear();

// Ждёт authChecked, чтобы не мелькнуть открытым при жёсткой перезагрузке до
// ответа /auth/me — у вернувшегося пользователя termsAcceptedAt на этот момент
// ещё не загружен в стор.
const showOnboarding = computed(
  () => authChecked.value && Boolean(auth.accessToken) && !auth.termsAcceptedAt
);

// Открытый маршрут: код из /route/<slug> (приоритет) или старый ?route=<id>.
// Код переводится в id через store/routeLinks (списки или GET /routes/by-slug).
const routeLinks = useRouteLinksStore();
const routeSlug = computed<string | null>(() => slugFromPath(route.path));
const legacyRouteId = computed<number | null>(() => {
  if (routeSlug.value) return null;
  const raw = route.query.route;
  const n = Number(Array.isArray(raw) ? raw[0] : raw);
  return Number.isFinite(n) && n > 0 ? n : null;
});
watch(
  routeSlug,
  (slug) => {
    if (slug) void routeLinks.resolveSlug(slug);
  },
  { immediate: true }
);
// Вошли/вышли - приватный маршрут по коду мог стать (не)доступным.
watch(() => auth.isAuthenticated, () => {
  routeLinks.forgetUnknown();
  if (routeSlug.value) void routeLinks.resolveSlug(routeSlug.value);
});
const selectedRouteId = computed<number | null>({
  get: () => (routeSlug.value ? routeLinks.idOf(routeSlug.value) : legacyRouteId.value),
  // Тап по линии на карте (не из поиска): replace, ожидающий возврат в поиск снимается.
  set: (id) => {
    void searchNav.selectRouteFromMap(id);
  },
});

// Карточка открыта, даже пока код ещё не переведён в id (чтобы не мигала панель поиска).
const hasRouteSelection = computed(() => routeSlug.value !== null || legacyRouteId.value !== null);
// ?point=<id> - карточка точки (поиск, «Точки рядом»; тап по точке на карте -
// MapView point-tap). Неизвестный/скрытый id - карточка в состоянии «не найдена».
const selectedPointId = computed<string | null>(() => {
  const raw = route.query.point;
  const v = Array.isArray(raw) ? raw[0] : raw;
  return v ? String(v) : null;
});
// Телефон - шторка точки, десктоп - карточка в левой панели (смонтирована одна).
const isPhone = useIsPhone();

// Поиск: сессия (текст/прокрутка/возврат из карточки) и переходы к объектам
// (в т.ч. «Назад» и крестик карточек - back()/close()).
const searchSession = useSearchSessionStore();
const searchNav = useSearchNavigation();
// Старую ссылку /map?route=<id> заменяем на /route/<код>.
watch(legacyRouteId, (id) => {
  if (id != null) void searchNav.normalizeLegacyRoute();
}, { immediate: true });


// Общий "курсор маршрута" (точка на выбранном маршруте, синхронизируется
// между перетаскиваемым маркером MapView и графиком высот RouteDetailsPanel,
// см. utils/routeCursor.ts). Временное состояние взаимодействия — намеренно
// простой ref, а не завязан на URL как selectedRouteId.
const routeCursorKm = ref<number | null>(null);
// Курсор - временный: пропадает (маркер на карте и бейдж на графике высот),
// если им не двигают - тем же сроком, что подсказка на карте.
const ROUTE_CURSOR_IDLE_MS = 2500;
let routeCursorIdleTimer: number | null = null;
watch(routeCursorKm, (km) => {
  if (routeCursorIdleTimer !== null) window.clearTimeout(routeCursorIdleTimer);
  routeCursorIdleTimer = null;
  if (km === null) return;
  routeCursorIdleTimer = window.setTimeout(() => {
    routeCursorIdleTimer = null;
    routeCursorKm.value = null;
  }, ROUTE_CURSOR_IDLE_MS);
});
onBeforeUnmount(() => {
  if (routeCursorIdleTimer !== null) window.clearTimeout(routeCursorIdleTimer);
});
// Смещение в км от одного маршрута бессмысленно для другого.
watch(selectedRouteId, () => {
  routeCursorKm.value = null;
});


const mobileDetailRef = ref<InstanceType<typeof MobileRouteDetailSheet> | null>(null);
const mobilePointRef = ref<InstanceType<typeof MobilePointSheet> | null>(null);
const mobileProfileRef = ref<InstanceType<typeof MobileProfileSheet> | null>(null);
// Тап по пустой карте / начало её перетаскивания (MapView map-interact):
// верхняя мобильная карточка сворачивается до заголовка - не закрывается, URL
// не меняется. На десктопе карточки в левой панели не трогаем.
const collapseMobileCard = () => {
  if (!isPhone.value) return;
  if (selectedPointId.value) mobilePointRef.value?.collapse();
  else if (selectedRouteId.value) mobileDetailRef.value?.collapse();
  else if (profileUsername.value) mobileProfileRef.value?.collapse();
};
// Открыта нижняя панель инструмента «Метка» (MapView.vue): шторки маршрута и
// панель поиска на это время скрыты.
const pointSheetOpen = ref(false);
const mobileOverlayMounted = computed(
  () => ready.value && activeTab.value === "map" && !hasRouteSelection.value && !selectedPointId.value && !profileUsername.value && !routeEditorActive.value
);
const mobileOverlayShown = computed(() => !pointSheetOpen.value && !listMode.value);
// Развёрнутый поиск на весь экран / панель поиска в покое над вкладками.
const mobileSearchOpen = computed(() => mobileOverlayMounted.value && mobileOverlayShown.value && searchSession.open);
// Над панелью действий стоит шторка маршрута вплотную - границу рисует она.
const routeBarShown = computed(
  () => !routeEditorActive.value && !!routeActions.current && activeTab.value === "map" && !listMode.value && !selectedPointId.value
);
const mobileDockShown = computed(() => mobileOverlayMounted.value && mobileOverlayShown.value && !searchSession.open);

const refreshMe = async (attempt = 0) => {
  if (!auth.accessToken) {
    authChecked.value = true;
    return;
  }
  try {
    const me = await api.get("/auth/me");
    auth.setProfile(me.data);
    savedRoutes.fetchIds();
  } catch (e) {
    if (isAuthRejection(e)) {
      auth.clear();
      savedRoutes.clear();
    } else if (attempt < 5) {
      // Сеть/5xx (например, сразу после пробуждения) - не выкидываем из
      // аккаунта, пробуем ещё раз чуть позже. authChecked остаётся false до
      // окончательного ответа, иначе страж /admin/* увидит пустые роли и
      // выкинет админа со страницы.
      setTimeout(() => refreshMe(attempt + 1), 3000 * (attempt + 1));
      return;
    }
  }
  authChecked.value = true;
};

// Прерванное входом действие - после /auth/me И списка сохранённых: иначе
// «сохранить» отработало бы по пустому списку, а ответ GET его затёр бы.
const onLogged = () =>
  loginGate.complete(
    async () => {
      await refreshMe();
      if (auth.isAuthenticated) await savedRoutes.fetchIds();
    },
    () => auth.isAuthenticated
  );

// Завершает отложенный возврат единой формы входа после ЛЮБОГО способа
// входа (модалка, Google/Telegram-редирект, подтверждение e-mail): как
// только состояние аутентифицировано и /auth/me отработал.
watch(
  [authChecked, () => auth.isAuthenticated],
  async ([checked, authed]) => {
    if (!checked || !authed) return;
    const result = await completePendingReturn(router);
    // Вошёл на /login, а вернуть некуда/не удалось - не оставляем на пустой странице.
    if (result !== "navigated" && route.name === "login") router.replace("/map");
  },
  { immediate: true }
);

// Посетитель без доступа к админке, попавший на /admin/* (прямой URL, старая
// закладка, кнопка "назад") — перенаправляется на карту после подтверждения
// через authChecked.
watch(
  [() => route.path, canOpenAdminPanel, authChecked],
  ([path, canAccess, checked]) => {
    if (checked && path.startsWith("/admin") && !canAccess) {
      router.replace("/map");
    }
  },
  { immediate: true }
);

const handleRouteSelect = (routeId: number | null) => {
  selectedRouteId.value = routeId;
  if (routeId != null) trackEvent("Route: View Card");
};

const selectTab = (key: TabKey) => {
  router.push("/" + key);
  if (key === "map") {
    sidebarCollapsed.value = false;
  }
};

const openRouteOnMap = async (routeId: number) => {
  const slug = await routeLinks.ensureSlug(routeId);
  router.push(slug ? routePath(slug) : { path: "/map", query: { route: String(routeId) } });
};

const openCamera = (cameraId: number) => {
  router.push({ path: "/live", query: { camera: String(cameraId) } });
};

const openNode = (_nodeId: number) => {
  router.push("/live");
};

onMounted(() => {
  ready.value = true;
  refreshMe();
  auth.bindCrossTabSync();
  // Планирует проактивный рефреш и для токена, уже сохранённого в
  // localStorage с прошлой сессии, не только для свежевыданного через
  // setTokens (см. store/auth.ts).
  auth.scheduleTokenRefresh();
  cookieConsent.init();
});
</script>
